/**
 * Layered bundle preview for bundle PDP media gallery.
 * Overlay transitions: Material fade-through crossfade (200ms out / 250ms in).
 */
const LAYER_IMG_CLASS = 'pointer-events-none absolute inset-0 w-full h-full object-cover object-center motion-reduce';

const VISUALIZER_WIDTHS = [
  200, 320, 450, 500, 550, 600, 650, 700, 750, 800, 850, 900, 1000, 1100, 1200, 1300, 1400, 1800, 2136, 2400, 3600,
  7680,
];

function imageUrlWithWidth(url, width) {
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.set('width', String(width));
    return parsed.href;
  } catch {
    return url.replace(/([?&])width=\d+/, `$1width=${width}`);
  }
}

function imageUrlKey(url) {
  if (!url) return '';
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.delete('width');
    parsed.searchParams.delete('height');
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return url
      .replace(/([?&])width=\d+/g, '')
      .replace(/([?&])height=\d+/g, '')
      .replace(/\?&/, '?')
      .replace(/[?&]$/, '');
  }
}

function snapToVisualizerWidth(pixelWidth) {
  for (const width of VISUALIZER_WIDTHS) {
    if (width >= pixelWidth) return width;
  }
  return VISUALIZER_WIDTHS[VISUALIZER_WIDTHS.length - 1];
}

function snapDownToVisualizerWidth(pixelWidth) {
  let result = VISUALIZER_WIDTHS[0];
  for (const width of VISUALIZER_WIDTHS) {
    if (width <= pixelWidth) result = width;
    else break;
  }
  return result;
}

function imageUrlsMatch(left, right) {
  return imageUrlKey(left) === imageUrlKey(right);
}

/** Single-width swap: matches preload URL so the browser hits cache after hover/idle preload. */
function applyDisplayImg(img, url, displayWidth) {
  if (!img || !url) return;
  img.removeAttribute('srcset');
  img.src = imageUrlWithWidth(url, displayWidth);
}

const MOTION = {
  exit: { duration: 200, easing: 'cubic-bezier(0.3, 0, 1, 1)' },
  enter: { duration: 250, easing: 'cubic-bezier(0, 0, 0, 1)' },
};

class BundleVisualizer {
  constructor(root) {
    this.root = root;
    this.bundleForm = document.querySelector('[data-bundle-form]');
    this.configEl = document.querySelector('[data-bundle-visualizer-config]');
    if (!this.bundleForm || !this.configEl) return;

    this.config = JSON.parse(this.configEl.textContent);
    this.debug = new URLSearchParams(window.location.search).has('debug_bundle_visualizer');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.enableTransitions = this.config.enableTransitions !== false && !this.reducedMotion;

    this.imageCache = new Map();
    this.resolvedUrls = new Set();
    this.badUrls = new Set();
    this.lastUrls = { base: null, duvet: null, sheets: null };
    this.previousUrls = { duvet: null, sheets: null };
    this.slotTransitionGen = { duvet: 0, sheets: 0 };
    this.hasRevealed = false;
    this.lastAccessibilityLabel = null;
    this.liveRegion = null;
    this.announceTimeout = null;

    this.maxConcurrent = 2;
    this.idleMaxConcurrent = 2;
    this.activeLoads = 0;
    this.activeLoadCounts = { urgent: 0, high: 0, low: 0 };
    this.loadQueues = { urgent: [], high: [], low: [] };

    this.debouncedUpdate = this.debounce(this.updateLayers.bind(this), 150);
    this.boundOnChange = (event) => {
      if (!this.bundleForm.contains(event.target)) return;
      const isLayerSwatch = Boolean(event.target.closest('[data-visualizer-layer-role]'));
      if (isLayerSwatch) {
        const layerName = event.target.closest('[data-visualizer-layer-role]')?.dataset.visualizerLayerRole;
        if (this.layerAppliedOnPointerDown === layerName) {
          this.syncLayerState({ focusVisualizer: true });
        } else {
          this.updateLayers({ focusVisualizer: true });
        }
        this.layerAppliedOnPointerDown = null;
        return;
      }
      this.debouncedUpdate();
    };
    this.boundOnSwatchHover = this.onSwatchHover.bind(this);
    this.boundOnFormPointerDown = this.onFormPointerDown.bind(this);
    this.backgroundLoadsPaused = false;
    this.layerAppliedOnPointerDown = null;

    this.init();
  }

  init() {
    this.lastUrls.base = this.config.baseUrl || null;
    this.revealInitialStack().then(() => {
      this.bundleForm.addEventListener('change', this.boundOnChange);
      this.bundleForm.addEventListener('pointerdown', this.boundOnFormPointerDown);
      this.bundleForm.querySelectorAll('[data-visualizer-layer-role] .swatch-label').forEach((label) => {
        label.addEventListener('pointerenter', this.boundOnSwatchHover);
        label.addEventListener('focusin', this.boundOnSwatchHover);
      });
    });
  }

  getStack() {
    return this.root.querySelector('[data-bundle-visualizer-stack]');
  }

  getLiveRegion() {
    if (!this.liveRegion) {
      this.liveRegion = this.root.closest('media-gallery')?.querySelector('[id^="GalleryStatus"]') || null;
    }
    return this.liveRegion;
  }

  getVisualizerState(urls) {
    const hasBase = Boolean(urls.base);
    if (!hasBase) return 'unavailable';
    if ((!urls.duvet && this.config.layers?.duvet) || (!urls.sheets && this.config.layers?.sheets)) {
      return 'partial';
    }
    if (!urls.duvet && !urls.sheets) return 'partial';
    return 'ready';
  }

  getLayerSelections() {
    const parts = [];

    Object.entries(this.config.layers || {}).forEach(([layerName, layerConfig]) => {
      const productScript = document.querySelector(`script[data-product-json-${layerConfig.productKey}]`);
      if (!productScript) return;

      try {
        const product = JSON.parse(productScript.textContent);
        const variant = this.findSelectedVariant(product, layerConfig.productKey);
        if (!variant) return;

        const fieldset = document.querySelector(`fieldset[data-bundle-form-input="${layerConfig.productKey}"]`);
        const checked = fieldset?.querySelector('input[type="radio"]:checked');
        const value = checked?.value?.trim();
        if (!value) return;

        const label = this.config.layerLabels?.[layerName] || layerName;
        parts.push(`${label}: ${value}`);
      } catch (e) {
        this.log('selection label error', layerName, e);
      }
    });

    return parts;
  }

  buildAccessibleLabel(urls) {
    const a11y = this.config.a11y || {};
    const state = this.getVisualizerState(urls);
    const selections = this.getLayerSelections();
    const selectionSuffix = selections.length ? `: ${selections.join(', ')}` : '';

    if (state === 'unavailable') {
      return a11y.previewUnavailable || this.root.getAttribute('aria-label') || a11y.preview || '';
    }
    if (state === 'partial') {
      const partial = a11y.previewPartial || a11y.preview || '';
      return selections.length ? `${partial}, ${selections.join(', ')}` : partial;
    }
    const preview = a11y.preview || '';
    return preview ? `${preview}${selectionSuffix}` : selectionSuffix.slice(2);
  }

  announcePreviewUpdate(selections) {
    const liveRegion = this.getLiveRegion();
    if (!liveRegion) return;

    const template = this.config.a11y?.previewUpdated || 'Bundle preview updated';
    const suffix = selections.length ? `: ${selections.join(', ')}` : '';

    liveRegion.setAttribute('aria-hidden', 'false');
    liveRegion.textContent = `${template}${suffix}`;
    clearTimeout(this.announceTimeout);
    this.announceTimeout = setTimeout(() => {
      liveRegion.setAttribute('aria-hidden', 'true');
    }, 2000);
  }

  updateAccessibility(urls, { announce = false } = {}) {
    const label = this.buildAccessibleLabel(urls);
    if (!label) return;

    const changed = label !== this.lastAccessibilityLabel;
    this.root.setAttribute('aria-label', label);

    if (announce && changed) {
      this.announcePreviewUpdate(this.getLayerSelections());
    }

    this.lastAccessibilityLabel = label;
  }

  getDisplayWidth() {
    const stack = this.getStack();
    const cssWidth = stack?.getBoundingClientRect().width || stack?.clientWidth || window.innerWidth;
    return snapToVisualizerWidth(Math.ceil(cssWidth * (window.devicePixelRatio || 1)));
  }

  getPreviewWidth(displayWidth = this.getDisplayWidth()) {
    const ratio = this.preferProgressiveLoading() ? 0.32 : 0.4;
    const target = this.preferProgressiveLoading()
      ? Math.min(320, Math.ceil(displayWidth * ratio))
      : Math.ceil(displayWidth * ratio);
    return snapDownToVisualizerWidth(target);
  }

  preferProgressiveLoading() {
    if (window.matchMedia('(max-width: 767px)').matches) return true;
    const conn = navigator.connection;
    if (!conn) return false;
    if (conn.saveData) return true;
    return ['slow-2g', '2g', '3g'].includes(conn.effectiveType);
  }

  getProgressivePreviewWidth(displayWidth = this.getDisplayWidth()) {
    const previewWidth = this.getPreviewWidth(displayWidth);
    return previewWidth < displayWidth ? previewWidth : null;
  }

  isUrlResolved(url, width) {
    return this.resolvedUrls.has(imageUrlWithWidth(url, width));
  }

  resolveDisplayUrl(url, width = this.getDisplayWidth()) {
    return imageUrlWithWidth(url, width);
  }

  isBadUrl(url) {
    return !url || this.badUrls.has(imageUrlKey(url));
  }

  async waitForImageReady(img) {
    if (!img) return;
    if (!img.complete || !img.naturalWidth) {
      await new Promise((resolve) => {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
      });
    }
    if (img.decode) {
      try {
        await img.decode();
      } catch {
        /* decode can fail on unsupported formats; still reveal */
      }
    }
  }

  async applyLayerImage(img, url, { transitionGen = null, layerName = null } = {}) {
    if (!img || this.isBadUrl(url)) return false;

    const displayWidth = this.getDisplayWidth();
    const previewWidth = this.getProgressivePreviewWidth(displayWidth);
    const usePreview = previewWidth && !this.isUrlResolved(url, displayWidth);

    if (usePreview) {
      this.log('progressive preview', layerName, previewWidth, '→', displayWidth);
      await this.preloadUrl(url, { width: previewWidth, priority: 'urgent' });
      if (this.isBadUrl(url)) return false;
      applyDisplayImg(img, url, previewWidth);
      await this.waitForImageReady(img);
      this.upgradeLayerImage(img, url, displayWidth, { transitionGen, layerName });
      return true;
    }

    await this.preloadUrl(url, { width: displayWidth, priority: 'urgent' });
    if (this.isBadUrl(url)) return false;
    applyDisplayImg(img, url, displayWidth);
    await this.waitForImageReady(img);
    return true;
  }

  async upgradeLayerImage(img, url, displayWidth, { transitionGen = null, layerName = null } = {}) {
    if (!img?.isConnected || this.isBadUrl(url) || !imageUrlsMatch(img.currentSrc || img.src, url)) return;

    const fullUrl = imageUrlWithWidth(url, displayWidth);
    if ((img.currentSrc || img.src) === fullUrl) return;

    if (transitionGen !== null && layerName !== null && transitionGen !== this.slotTransitionGen[layerName]) return;

    if (!this.isUrlResolved(url, displayWidth)) {
      await this.preloadUrl(url, { width: displayWidth, priority: 'low' });
    }

    if (transitionGen !== null && layerName !== null && transitionGen !== this.slotTransitionGen[layerName]) return;
    if (!img.isConnected || this.isBadUrl(url) || !imageUrlsMatch(img.currentSrc || img.src, url)) return;

    const slot = img.closest('[data-layer-slot]');
    if (!slot || slot.querySelector('[data-layer-active]') !== img) return;

    this.trace('progressive upgrade start', { layerName, displayWidth });

    const upgradeImg = this.createLayerImg();
    upgradeImg.src = fullUrl;
    await this.waitForImageReady(upgradeImg);

    if (transitionGen !== null && layerName !== null && transitionGen !== this.slotTransitionGen[layerName]) {
      upgradeImg.remove();
      return;
    }
    if (!img.isConnected || slot.querySelector('[data-layer-active]') !== img) {
      upgradeImg.remove();
      return;
    }

    // Swap elements instead of changing src — Safari flashes on in-place src upgrades.
    slot.appendChild(upgradeImg);
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    if (img.isConnected) img.remove();

    this.log('progressive upgrade', layerName, displayWidth);
    this.trace('progressive upgrade done', { layerName, displayWidth });
  }

  async setImgSrc(img, url) {
    if (!img || this.isBadUrl(url)) return false;
    if (imageUrlsMatch(img.currentSrc || img.src, url)) {
      await this.waitForImageReady(img);
      return true;
    }
    return this.applyLayerImage(img, url);
  }

  /** Trust Liquid srcset when the image file already matches; only fetch on variant mismatch. */
  async syncInitialLayer(img, url, { layerName = null, slot = null } = {}) {
    if (layerName) {
      url = this.resolveLayerUrlWithFallback(url, layerName);
    }
    if (!url) return;

    if (!img && slot) {
      img = this.createLayerImg();
      slot.appendChild(img);
    }
    if (!img) return;

    if (imageUrlsMatch(img.currentSrc || img.src, url)) {
      await this.waitForImageReady(img);
      if (slot) {
        slot.dataset.currentUrl = url;
        if (layerName) this.previousUrls[layerName] = url;
      }
      return;
    }

    const loaded = await this.setImgSrc(img, url);
    if (loaded) {
      if (slot) {
        slot.dataset.currentUrl = url;
        if (layerName) this.previousUrls[layerName] = url;
      }
    } else if (slot && layerName) {
      img.remove();
      delete slot.dataset.currentUrl;
    }
  }

  async syncInitialSlot(layerName, url) {
    const slot = this.getLayerSlot(layerName);
    if (!slot || !this.config.layers?.[layerName]) return;

    const img = slot.querySelector('[data-layer-active]');
    await this.syncInitialLayer(img, url, { layerName, slot });
  }

  async syncInitialBase(url) {
    const baseImg = this.root.querySelector('img[data-layer="base"]');
    if (!baseImg || !url) return;
    await this.syncInitialLayer(baseImg, url);
  }

  async revealInitialStack() {
    const stack = this.getStack();
    if (!stack) return;

    const urls = this.resolveAllLayerUrls();
    this.setVisualizerState(urls);

    await Promise.all([
      this.syncInitialBase(urls.base),
      this.syncInitialSlot('duvet', urls.duvet),
      this.syncInitialSlot('sheets', urls.sheets),
    ]);

    const imgs = [
      this.root.querySelector('img[data-layer="base"]'),
      ...['duvet', 'sheets']
        .map((name) => this.getLayerSlot(name)?.querySelector('[data-layer-active]'))
        .filter(Boolean),
    ];

    await Promise.all(imgs.map((img) => this.waitForImageReady(img)));

    this.lastUrls = { ...urls };
    this.hasRevealed = true;
    stack.dataset.visualizerRevealed = 'true';
    stack.classList.remove('opacity-0');

    this.scheduleTier1();
  }

  debounce(fn, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), wait);
    };
  }

  log(...args) {
    if (this.debug) console.log('[bundle-visualizer]', ...args);
  }

  trace(event, detail = {}) {
    if (!this.debug) return;
    const now = performance.now();
    if (this._traceOrigin == null) {
      this._traceOrigin = now;
      this._traceSeq = 0;
    }
    this._traceSeq += 1;
    console.log(`[bundle-visualizer] #${this._traceSeq} +${Math.round(now - this._traceOrigin)}ms`, event, detail);
  }

  resetTrace(origin = 'swatch') {
    if (!this.debug) return;
    this._traceOrigin = performance.now();
    this._traceSeq = 0;
    this.trace('trace reset', { origin });
  }

  getLayerSlot(name) {
    return this.root.querySelector(`[data-layer-slot="${name}"]`);
  }

  findSelectedVariant(product, productKey) {
    const options = [];
    const fieldset = document.querySelector(`fieldset[data-bundle-form-input="${productKey}"]`);
    const scope = fieldset?.closest('[data-bundle-product-container]') || document;

    if (fieldset) {
      fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
        if (!input.checked) return;
        const optionName = input.getAttribute('data-option-name') || fieldset.dataset.optionName;
        if (optionName) {
          const optionIndex = product.options.indexOf(optionName);
          if (optionIndex !== -1) options[optionIndex] = input.value;
        }
      });
    }

    scope.querySelectorAll(`[data-bundle-form-hidden-input="${productKey}"]`).forEach((input) => {
      const match = input.dataset.variantOption?.match(/option(\d+)/);
      if (match) options[parseInt(match[1], 10) - 1] = input.value;
    });

    return product.variants.find((variant) =>
      options.every((option, index) => option === undefined || variant[`option${index + 1}`] === option),
    );
  }

  getMetafieldsJson(productKey) {
    return document.querySelector(`script[data-product-metafields-json-${productKey}]`);
  }

  getLayerUrlFromVariant(variantId, productKey, metafieldKey) {
    const script = this.getMetafieldsJson(productKey);
    if (!script) return null;
    try {
      const data = JSON.parse(script.textContent);
      const url = data?.variants?.[variantId]?.metafields?.custom?.[metafieldKey]?.url;
      return url || null;
    } catch (e) {
      this.log('metafields parse error', e);
      return null;
    }
  }

  resolveLayerUrl(layerName) {
    const layerConfig = this.config.layers?.[layerName];
    if (!layerConfig) return null;

    const productScript = document.querySelector(`script[data-product-json-${layerConfig.productKey}]`);
    if (!productScript) return null;

    try {
      const product = JSON.parse(productScript.textContent);
      const variant = this.findSelectedVariant(product, layerConfig.productKey);
      if (!variant?.id) return null;
      return this.getLayerUrlFromVariant(String(variant.id), layerConfig.productKey, layerConfig.metafieldKey);
    } catch (e) {
      this.log('resolve error', layerName, e);
      return null;
    }
  }

  resolveAllLayerUrls() {
    return {
      base: this.config.baseUrl || null,
      duvet: this.resolveLayerUrl('duvet'),
      sheets: this.resolveLayerUrl('sheets'),
    };
  }

  collectAvailableLayerUrls() {
    const urls = new Set();

    Object.entries(this.config.layers || {}).forEach(([layerName, layerConfig]) => {
      const productScript = document.querySelector(`script[data-product-json-${layerConfig.productKey}]`);
      const metafieldsScript = this.getMetafieldsJson(layerConfig.productKey);
      if (!metafieldsScript) return;

      try {
        const metafields = JSON.parse(metafieldsScript.textContent);
        let variantEntries = Object.entries(metafields?.variants || {});

        if (productScript) {
          const product = JSON.parse(productScript.textContent);
          const availableIds = new Set(product.variants.filter((v) => v.available !== false).map((v) => String(v.id)));
          variantEntries = variantEntries.filter(([id]) => availableIds.has(id));
        }

        variantEntries.forEach(([, variantData]) => {
          const url = variantData?.metafields?.custom?.[layerConfig.metafieldKey]?.url;
          if (url) urls.add(url);
        });
      } catch (e) {
        this.log('collect urls error', layerName, e);
      }
    });

    return [...urls];
  }

  getQueuedLoadCount() {
    return this.loadQueues.urgent.length + this.loadQueues.high.length + this.loadQueues.low.length;
  }

  hasInteractivePriorityWork() {
    return (
      this.activeLoadCounts.urgent > 0 ||
      this.activeLoadCounts.high > 0 ||
      this.loadQueues.urgent.length > 0 ||
      this.loadQueues.high.length > 0
    );
  }

  getLoadLimit(requestPriority = 'low') {
    if (requestPriority === 'urgent' || requestPriority === 'high') return this.maxConcurrent;
    if (this.backgroundLoadsPaused) return 0;
    return this.hasInteractivePriorityWork() ? 0 : this.idleMaxConcurrent;
  }

  enqueueLoad(run, priority = 'high') {
    if (this.activeLoads < this.getLoadLimit(priority)) {
      run();
      return;
    }

    const queue = this.loadQueues[priority] ?? this.loadQueues.high;
    queue.push(run);
    this.trace('preload queued', { priority, activeLoads: this.activeLoads, queue: this.getQueuedLoadCount() });
  }

  preloadUrl(url, { priority = 'high', width = null } = {}) {
    if (this.isBadUrl(url)) {
      return Promise.resolve(null);
    }

    const resolvedWidth = width ?? this.getDisplayWidth();
    const displayUrl = imageUrlWithWidth(url, resolvedWidth);
    if (this.imageCache.has(displayUrl)) {
      this.trace('preload cache', { priority, width: resolvedWidth, cached: this.resolvedUrls.has(displayUrl) });
      return this.imageCache.get(displayUrl);
    }

    const promise = new Promise((resolve) => {
      const startedAt = performance.now();
      const run = () => {
        this.activeLoads += 1;
        this.activeLoadCounts[priority] = (this.activeLoadCounts[priority] || 0) + 1;
        this.trace('preload start', { priority, width: resolvedWidth, activeLoads: this.activeLoads });
        const img = new Image();
        const finish = (ok) => {
          this.activeLoads -= 1;
          this.activeLoadCounts[priority] = Math.max(0, (this.activeLoadCounts[priority] || 0) - 1);
          this.trace(ok ? 'preload done' : 'preload error', {
            priority,
            width: resolvedWidth,
            ms: Math.round(performance.now() - startedAt),
            activeLoads: this.activeLoads,
          });
          this.processQueue();
          resolve(ok ? img : null);
        };
        img.onload = () => {
          this.resolvedUrls.add(displayUrl);
          finish(true);
        };
        img.onerror = () => {
          this.badUrls.add(imageUrlKey(url));
          this.log('preload failed', displayUrl);
          finish(false);
        };
        img.src = displayUrl;
      };

      this.enqueueLoad(run, priority);
    });

    this.imageCache.set(displayUrl, promise);
    return promise;
  }

  processQueue() {
    const priorities = ['urgent', 'high', 'low'];
    while (true) {
      let dispatched = false;
      for (const priority of priorities) {
        if (!this.loadQueues[priority].length) continue;
        if (this.activeLoads >= this.getLoadLimit(priority)) return;
        this.loadQueues[priority].shift()();
        dispatched = true;
        break;
      }
      if (!dispatched) break;
    }
  }

  pauseBackgroundLoads() {
    this.backgroundLoadsPaused = true;
  }

  resumeBackgroundLoads() {
    this.backgroundLoadsPaused = false;
    this.processQueue();
  }

  preloadTier0(urls) {
    [urls.base, urls.duvet, urls.sheets].filter(Boolean).forEach((url) => this.preloadUrl(url, { priority: 'high' }));
  }

  scheduleBackgroundPreload(urls, { skipLayers = [] } = {}) {
    const skip = new Set(skipLayers);
    const run = () => {
      if (this.backgroundLoadsPaused) return;
      if (urls.base) this.preloadUrl(urls.base, { priority: 'low' });
      ['duvet', 'sheets'].forEach((name) => {
        if (!urls[name] || skip.has(name)) return;
        const width = this.preferProgressiveLoading() ? this.getPreviewWidth() : null;
        this.preloadUrl(urls[name], { priority: 'low', width });
      });
    };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(run, { timeout: 2000 });
    } else {
      setTimeout(run, 300);
    }
  }

  scheduleTier1() {
    const run = () => {
      if (this.backgroundLoadsPaused) return;
      const width = this.preferProgressiveLoading() ? this.getPreviewWidth() : null;
      this.collectAvailableLayerUrls().forEach((url) => this.preloadUrl(url, { priority: 'low', width }));
    };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(run, { timeout: 3000 });
    } else {
      setTimeout(run, 500);
    }
  }

  resolveSwatchLayerUrl(input) {
    const container = input.closest('[data-visualizer-layer-role]');
    if (!container) return null;

    const role = container.dataset.visualizerLayerRole;
    const layerConfig = this.config.layers?.[role];
    if (!layerConfig) return null;

    const productScript = document.querySelector(`script[data-product-json-${layerConfig.productKey}]`);
    if (!productScript) return null;

    try {
      const product = JSON.parse(productScript.textContent);
      const fieldset = input.closest('fieldset');
      const optionName = fieldset?.dataset.optionName;
      const optionIndex = optionName ? product.options.indexOf(optionName) : -1;
      const variant = product.variants.find((v) => {
        if (optionIndex >= 0) return v[`option${optionIndex + 1}`] === input.value;
        return [v.option1, v.option2, v.option3].includes(input.value);
      });
      if (!variant) return null;
      return this.getLayerUrlFromVariant(String(variant.id), layerConfig.productKey, layerConfig.metafieldKey);
    } catch (e) {
      this.log('swatch url resolve', e);
      return null;
    }
  }

  resolveSwatchInputFromEvent(event) {
    const layer = event.target.closest('[data-visualizer-layer-role]');
    if (!layer) return null;

    const direct = event.target.closest('input[type="radio"]');
    if (direct && layer.contains(direct)) return direct;

    const label =
      event.target.closest('label.swatch-label') ??
      (event.currentTarget?.matches?.('label.swatch-label') ? event.currentTarget : null);
    if (!label?.htmlFor || !layer.contains(label)) return null;

    const input = document.getElementById(label.htmlFor);
    return input?.type === 'radio' ? input : null;
  }

  preloadSwatchIntent(input, { priority = 'low' } = {}) {
    const url = this.resolveSwatchLayerUrl(input);
    if (!url) return;

    const displayWidth = this.getDisplayWidth();
    const previewWidth = this.getProgressivePreviewWidth(displayWidth);
    const usePreview = Boolean(previewWidth) && priority === 'urgent';
    const width = usePreview ? previewWidth : this.preferProgressiveLoading() ? previewWidth || displayWidth : displayWidth;
    this.preloadUrl(url, { priority, width });
  }

  onFormPointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const input = this.resolveSwatchInputFromEvent(event);
    if (!input || input.checked) return;

    const layerName = input.closest('[data-visualizer-layer-role]')?.dataset.visualizerLayerRole;
    const url = this.resolveSwatchLayerUrl(input);
    if (!layerName || !url) return;

    this.resetTrace('pointerdown');
    this.pauseBackgroundLoads();
    this.preloadSwatchIntent(input, { priority: 'urgent' });
    this.trace('swatch pointerdown', { value: input.value, layerName });

    this.layerAppliedOnPointerDown = layerName;
    this.applyLayerSlot(this.getLayerSlot(layerName), url, layerName);
  }

  onSwatchHover(event) {
    if (!window.matchMedia('(hover: hover)').matches) return;

    const input = this.resolveSwatchInputFromEvent(event);
    if (!input) return;

    this.preloadSwatchIntent(input, { priority: 'low' });
  }
  setVisualizerState(urls, { announce = false } = {}) {
    const state = this.getVisualizerState(urls);
    this.root.dataset.visualizerState = state;
    this.root.querySelector('[data-bundle-visualizer-stack]')?.classList.toggle('min-h-48', state === 'unavailable');
    this.updateAccessibility(urls, { announce });
  }

  createLayerImg() {
    const img = document.createElement('img');
    img.className = LAYER_IMG_CLASS;
    img.alt = '';
    img.decoding = 'async';
    img.dataset.layerActive = '';
    return img;
  }

  waitForImageLoad(img, url, width = this.getDisplayWidth()) {
    return new Promise((resolve) => {
      const onLoad = () => {
        cleanup();
        resolve(true);
      };
      const onError = () => {
        cleanup();
        this.badUrls.add(imageUrlKey(url));
        this.log('image load failed', url);
        resolve(false);
      };
      const cleanup = () => {
        img.removeEventListener('load', onLoad);
        img.removeEventListener('error', onError);
      };
      img.addEventListener('load', onLoad);
      img.addEventListener('error', onError);
      applyDisplayImg(img, url, width);
      if (img.complete && img.naturalWidth) onLoad();
    });
  }

  cancelSlotAnimations(slot) {
    slot.querySelectorAll('img').forEach((img) => {
      img.getAnimations?.().forEach((anim) => anim.cancel());
    });
  }

  async animateExit(img) {
    if (!this.enableTransitions) {
      img.remove();
      return;
    }
    const animation = img.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: MOTION.exit.duration,
      easing: MOTION.exit.easing,
      fill: 'forwards',
    });
    await animation.finished.catch(() => {});
    img.remove();
  }

  async animateEnter(img) {
    img.style.opacity = '0';

    if (!this.enableTransitions) {
      img.style.opacity = '1';
      return;
    }

    const animation = img.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: MOTION.enter.duration,
      easing: MOTION.enter.easing,
      fill: 'forwards',
    });
    await animation.finished.catch(() => {});
    img.style.opacity = '1';
  }

  resolveLayerUrlWithFallback(url, layerName) {
    if (url && !this.isBadUrl(url)) return url;
    if (url && this.isBadUrl(url)) {
      const fallback = this.previousUrls[layerName];
      if (fallback && !this.isBadUrl(fallback)) return fallback;
    }
    return null;
  }

  async applyLayerSlot(slot, url, layerName, { skipTransition = false } = {}) {
    if (!slot) return;

    url = this.resolveLayerUrlWithFallback(url, layerName);

    if (url && slot.dataset.currentUrl === url) {
      if (skipTransition) this.previousUrls[layerName] = url;
      return;
    }

    const transitionGen = ++this.slotTransitionGen[layerName];
    this.cancelSlotAnimations(slot);

    if (!url) {
      const active = slot.querySelector('[data-layer-active]');
      if (active && !skipTransition && this.enableTransitions) {
        await this.animateExit(active);
      } else {
        slot.querySelectorAll('img').forEach((img) => img.remove());
      }
      if (transitionGen === this.slotTransitionGen[layerName]) {
        delete slot.dataset.currentUrl;
      }
      return;
    }

    const currentImg = slot.querySelector('[data-layer-active]');
    const displayWidth = this.getDisplayWidth();
    const previewWidth = this.getProgressivePreviewWidth(displayWidth);
    const usePreview = Boolean(previewWidth) && !this.isUrlResolved(url, displayWidth);
    this.trace('applyLayerSlot', {
      layerName,
      usePreview,
      previewWidth,
      displayWidth,
      fullResolved: this.isUrlResolved(url, displayWidth),
      previewResolved: previewWidth ? this.isUrlResolved(url, previewWidth) : false,
    });

    if (skipTransition || !this.enableTransitions) {
      this.trace('applyLayerSlot skip transition', { layerName, skipTransition, enableTransitions: this.enableTransitions });
      let img = slot.querySelector('[data-layer-active]');
      if (!img) {
        img = this.createLayerImg();
        slot.appendChild(img);
      }

      if (url && imageUrlsMatch(img.currentSrc || img.src, url)) {
        slot.dataset.currentUrl = url;
        this.previousUrls[layerName] = url;
        return;
      }

      const loaded = await this.applyLayerImage(img, url, { transitionGen, layerName });
      if (transitionGen === this.slotTransitionGen[layerName] && loaded) {
        slot.dataset.currentUrl = url;
        this.previousUrls[layerName] = url;
      } else if (!loaded) {
        img.remove();
        delete slot.dataset.currentUrl;
      }
      return;
    }

    const incomingImg = this.createLayerImg();
    incomingImg.style.opacity = '0';
    slot.appendChild(incomingImg);

    const loadWidth = usePreview ? previewWidth : displayWidth;
    this.trace('transition start', { layerName, loadWidth, usePreview });
    const fullPreloadPromise = usePreview
      ? this.preloadUrl(url, { width: displayWidth, priority: 'low' })
      : null;

    if (usePreview) {
      this.log('progressive preview', layerName, previewWidth, '→', displayWidth);
      await this.preloadUrl(url, { width: previewWidth, priority: 'urgent' });
      if (this.isBadUrl(url)) {
        incomingImg.remove();
        return;
      }
      if (transitionGen !== this.slotTransitionGen[layerName]) {
        incomingImg.remove();
        return;
      }
      if (!(await this.waitForImageLoad(incomingImg, url, previewWidth))) {
        incomingImg.remove();
        return;
      }
    } else {
      await this.preloadUrl(url, { width: displayWidth, priority: 'urgent' });
      if (this.isBadUrl(url)) {
        this.log('skipping bad url', layerName, url);
        incomingImg.remove();
        return;
      }
      if (transitionGen !== this.slotTransitionGen[layerName]) {
        incomingImg.remove();
        return;
      }
      if (!(await this.waitForImageLoad(incomingImg, url, displayWidth))) {
        incomingImg.remove();
        return;
      }
    }

    if (transitionGen !== this.slotTransitionGen[layerName]) {
      incomingImg.remove();
      return;
    }

    try {
      await incomingImg.decode();
    } catch {
      /* decode can fail on unsupported formats */
    }
    incomingImg.style.opacity = '0';
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    const exitPromise = currentImg ? this.animateExit(currentImg) : Promise.resolve();
    const enterPromise = this.animateEnter(incomingImg);

    await Promise.all([exitPromise, enterPromise]);
    if (usePreview) {
      if (fullPreloadPromise) await fullPreloadPromise.catch(() => {});
      this.upgradeLayerImage(incomingImg, url, displayWidth, { transitionGen, layerName }).catch(() => {});
    }
    this.trace('layer visible', { layerName, usePreview });

    if (transitionGen === this.slotTransitionGen[layerName]) {
      slot.dataset.currentUrl = url;
      this.previousUrls[layerName] = url;
    }
  }

  applyLayers(urls, { skipTransition = false, announce = false } = {}) {
    if (!this.hasRevealed) return;

    this.setVisualizerState(urls, { announce });

    const baseImg = this.root.querySelector('img[data-layer="base"]');
    if (urls.base && baseImg && !imageUrlsMatch(baseImg.currentSrc || baseImg.src, urls.base)) {
      applyDisplayImg(baseImg, urls.base, this.getDisplayWidth());
    }

    ['duvet', 'sheets'].forEach((name) => {
      if (!this.config.layers?.[name]) return;
      if (urls[name] === this.lastUrls[name] && skipTransition) return;
      if (urls[name] !== this.lastUrls[name] || skipTransition) {
        this.applyLayerSlot(this.getLayerSlot(name), urls[name], name, { skipTransition });
      }
    });

    this.lastUrls = { ...urls };
  }

  focusVisualizerSlide() {
    const gallery = this.root.closest('media-gallery');
    if (!gallery?.goToVisualizerSlide || gallery.isOnVisualizerSlide()) return;
    requestAnimationFrame(() => {
      if (!gallery.isOnVisualizerSlide()) gallery.goToVisualizerSlide();
    });
  }

  syncLayerState({ focusVisualizer = false } = {}) {
    this.trace('syncLayerState', { focusVisualizer });
    const urls = this.resolveAllLayerUrls();
    const changedLayers = ['duvet', 'sheets'].filter((name) => urls[name] !== this.lastUrls[name]);
    this.resumeBackgroundLoads();
    this.setVisualizerState(urls, { announce: true });
    this.lastUrls = { ...urls };
    this.scheduleBackgroundPreload(urls, { skipLayers: changedLayers });
    if (focusVisualizer) this.focusVisualizerSlide();
  }

  updateLayers({ focusVisualizer = false } = {}) {
    this.trace('updateLayers', { focusVisualizer });
    const urls = this.resolveAllLayerUrls();
    const changedLayers = ['duvet', 'sheets'].filter((name) => urls[name] !== this.lastUrls[name]);
    this.resumeBackgroundLoads();
    this.applyLayers(urls, { announce: true });
    this.preloadTier0(urls);
    this.scheduleBackgroundPreload(urls, { skipLayers: changedLayers });
    if (focusVisualizer) this.focusVisualizerSlide();
  }

  destroy() {
    clearTimeout(this.announceTimeout);
    this.bundleForm?.removeEventListener('change', this.boundOnChange);
    this.bundleForm?.removeEventListener('pointerdown', this.boundOnFormPointerDown);
  }
}

if (!customElements.get('bundle-visualizer')) {
  customElements.define(
    'bundle-visualizer',
    class BundleVisualizerElement extends HTMLElement {
      connectedCallback() {
        if (!document.querySelector('[data-bundle-visualizer-config]')) return;
        this.visualizer = new BundleVisualizer(this);
      }

      disconnectedCallback() {
        this.visualizer?.destroy();
      }
    },
  );
}

